import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";

import SanitizeHtml from "components/SanitizeHtml";
import Stack from "components/Stack";
import Text from "components/Text";

import SectionHeader from "entities/Memo/components/Sections/SectionHeader";
import { IMemoSection } from "entities/Memo/sdk";
import { useAITableDisplayMode } from "entities/Memo/utils";

export interface IPrintableSectionAIAnswerr {
  section: IMemoSection;
  externalToken?: string;
}
const PrintableSectionAIAnswer = ({
  section,
  externalToken,
}: IPrintableSectionAIAnswerr) => {
  const params = useParams<{
    memoId: string;
    dealId: string;
  }>();
  const [searchParams] = useSearchParams();

  const dealId = useMemo(() => Number(params.dealId), [params.dealId]);
  const memoSectionId = useMemo(
    () =>
      searchParams.get("memoSectionId")
        ? Number(searchParams.get("memoSectionId"))
        : null,
    [searchParams]
  );

  const { sanitizeHtmlOptions, sanitizeHtmlContainerStyles } =
    useAITableDisplayMode({
      dealId,
      externalToken,
    });

  const field = useMemo(
    () => (!_.isNil(_.first(section.fields)) ? _.first(section.fields) : null),
    [section]
  );

  const fieldValue = useMemo(() => {
    if (section.is_prompt_response_in_progress) {
      return null;
    }
    if (
      !_.isNil(field?.values) &&
      !_.isEmpty(field?.values) &&
      !section.is_prompt_response_in_progress
    ) {
      return _.get(_.first(field.values), "rich_value", null);
    }
    return null;
  }, [field?.values, section.is_prompt_response_in_progress]);

  const placeholder = useMemo(
    () =>
      section.is_prompt_response_in_progress
        ? "An answer is currently being generated..."
        : "AI-generated answer | No content yet...",
    [section.is_prompt_response_in_progress]
  );

  return _.isNil(field) ? null : (
    <Stack gap={2}>
      <SectionHeader
        label={
          <Stack direction="row" alignItems="center" gap={0.8}>
            <Text
              variant="text1"
              sx={{ fontWeight: "bold", display: "inline" }}
            >
              {section.title}
            </Text>
          </Stack>
        }
        isFocused={section.section_id === memoSectionId}
        alignActionButton={section ? "right" : "left"}
      />
      <Stack
        padding={theme.spacing(0, 2)}
        sx={{
          "*": { lineHeight: "140% !important" },
          table: {
            width: "100%",
          },
          "table, th, td, tr": {
            borderCollapse: "collapse",
            border: "1px solid black !important",
          },
          ...sanitizeHtmlContainerStyles,
        }}
      >
        {!_.isNil(fieldValue) ? (
          <SanitizeHtml html={fieldValue} options={sanitizeHtmlOptions} />
        ) : (
          <Text sx={{ color: colors.gray60 }}>{placeholder}</Text>
        )}
      </Stack>
    </Stack>
  );
};

export default PrintableSectionAIAnswer;
